package com.fantasytenis.web.screens.auth

import androidx.compose.runtime.*
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.TextField
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.h2Style
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Password validation requirements
 */
private data class PasswordValidation(
    val hasMinLength: Boolean,
    val hasUppercase: Boolean,
    val hasLowercase: Boolean,
    val hasNumber: Boolean,
    val hasSpecialChar: Boolean
) {
    val isValid: Boolean
        get() = hasMinLength && hasUppercase && hasLowercase && hasNumber

    companion object {
        fun validate(password: String): PasswordValidation {
            return PasswordValidation(
                hasMinLength = password.length >= 8,
                hasUppercase = password.any { it.isUpperCase() },
                hasLowercase = password.any { it.isLowerCase() },
                hasNumber = password.any { it.isDigit() },
                hasSpecialChar = password.any { !it.isLetterOrDigit() }
            )
        }
    }
}

/**
 * Username validation result
 */
internal data class UsernameValidation(
    val isFormatValid: Boolean,
    val isChecking: Boolean,
    val isAvailable: Boolean?,
    val suggestion: String?,
    val errorMessage: String?
) {
    val isValid: Boolean
        get() = isFormatValid && isAvailable == true

    companion object {
        private val usernameRegex = Regex("^[a-z][a-z0-9_]*$")

        fun validateFormat(username: String): UsernameValidation {
            val trimmed = username.lowercase().trim()

            return when {
                trimmed.isEmpty() -> UsernameValidation(
                    isFormatValid = false,
                    isChecking = false,
                    isAvailable = null,
                    suggestion = null,
                    errorMessage = null
                )
                trimmed.length < 3 -> UsernameValidation(
                    isFormatValid = false,
                    isChecking = false,
                    isAvailable = null,
                    suggestion = null,
                    errorMessage = "Username must be at least 3 characters"
                )
                trimmed.length > 20 -> UsernameValidation(
                    isFormatValid = false,
                    isChecking = false,
                    isAvailable = null,
                    suggestion = null,
                    errorMessage = "Username must be 20 characters or less"
                )
                !usernameRegex.matches(trimmed) -> UsernameValidation(
                    isFormatValid = false,
                    isChecking = false,
                    isAvailable = null,
                    suggestion = null,
                    errorMessage = "Username must start with a letter and contain only letters, numbers, and underscores"
                )
                else -> UsernameValidation(
                    isFormatValid = true,
                    isChecking = true,
                    isAvailable = null,
                    suggestion = null,
                    errorMessage = null
                )
            }
        }
    }
}

@Composable
fun RegisterScreen(
    onRegisterSuccess: () -> Unit,
    onNavigateToLogin: () -> Unit
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var firstName by remember { mutableStateOf("") }
    var lastName by remember { mutableStateOf("") }
    var username by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var showPasswordRequirements by remember { mutableStateOf(false) }
    var usernameValidation by remember { mutableStateOf(UsernameValidation.validateFormat("")) }
    var usernameCheckJob by remember { mutableStateOf<Job?>(null) }

    val scope = rememberCoroutineScope()

    // Real-time password validation
    val passwordValidation = remember(password) {
        PasswordValidation.validate(password)
    }

    // Real-time username validation with debounced API check
    LaunchedEffect(username) {
        usernameCheckJob?.cancel()
        val formatValidation = UsernameValidation.validateFormat(username)
        usernameValidation = formatValidation

        if (formatValidation.isFormatValid) {
            usernameCheckJob = scope.launch {
                delay(500) // Debounce 500ms
                DependencyContainer.authRepository.checkUsernameAvailable(username.lowercase())
                    .onSuccess { result ->
                        usernameValidation = UsernameValidation(
                            isFormatValid = true,
                            isChecking = false,
                            isAvailable = result.available,
                            suggestion = result.suggestion.takeIf { it.isNotBlank() },
                            errorMessage = if (!result.available) "Username is already taken" else null
                        )
                    }
                    .onFailure {
                        usernameValidation = formatValidation.copy(isChecking = false)
                    }
            }
        }
    }

    val isFormValid = email.isNotBlank() &&
            password.isNotBlank() &&
            passwordValidation.isValid &&
            confirmPassword.isNotBlank() &&
            password == confirmPassword &&
            firstName.isNotBlank() &&
            lastName.isNotBlank() &&
            usernameValidation.isValid

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("min-height", "var(--app-height)")
            padding(24.px)
            overflowY("auto")
            property("-webkit-overflow-scrolling", "touch")
        }
    }) {
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(12.px)
                padding(48.px)
                property("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)")
                width(100.percent)
                maxWidth(400.px)
            }
        }) {
            // Title
            H2({
                style {
                    h2Style()
                    textAlign("center")
                    marginBottom(32.px)
                    color(Colors.Primary)
                }
            }) {
                Text("Create Account")
            }

            // Name Fields
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(16.px)
                }
            }) {
                Div({ style { flex(1) } }) {
                    TextField(
                        value = firstName,
                        onValueChange = { firstName = it },
                        label = "First Name",
                        placeholder = "John"
                    )
                }
                Div({ style { flex(1) } }) {
                    TextField(
                        value = lastName,
                        onValueChange = { lastName = it },
                        label = "Last Name",
                        placeholder = "Doe"
                    )
                }
            }

            // Email Field
            TextField(
                value = email,
                onValueChange = { email = it },
                label = "Email",
                type = "email",
                placeholder = "john@example.com"
            )

            // Username Field
            Div({
                style {
                    marginBottom(16.px)
                }
            }) {
                TextField(
                    value = username,
                    onValueChange = { username = it.lowercase().filter { c -> c.isLetterOrDigit() || c == '_' } },
                    label = "Username",
                    placeholder = "johndoe",
                    error = usernameValidation.errorMessage
                )

                // Username status indicator
                if (username.isNotBlank()) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            marginTop(4.px)
                            fontSize(12.px)
                        }
                    }) {
                        when {
                            usernameValidation.isChecking -> {
                                Span({ style { color(Colors.TextSecondary) } }) {
                                    Text("Checking availability...")
                                }
                            }
                            usernameValidation.isAvailable == true -> {
                                Span({ style { color(Colors.Success) } }) {
                                    Text("\u2713 @${username.lowercase()} is available")
                                }
                            }
                            usernameValidation.isAvailable == false && usernameValidation.suggestion != null -> {
                                Span({ style { color(Colors.TextSecondary) } }) {
                                    Text("Try: ")
                                    A(
                                        href = "#",
                                        attrs = {
                                            onClick { e ->
                                                e.preventDefault()
                                                username = usernameValidation.suggestion!!
                                            }
                                            style {
                                                color(Colors.Accent)
                                                fontWeight(500)
                                            }
                                        }
                                    ) {
                                        Text("@${usernameValidation.suggestion}")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Password Field
            TextField(
                value = password,
                onValueChange = {
                    password = it
                    showPasswordRequirements = true
                },
                label = "Password",
                type = "password",
                placeholder = "Enter a strong password",
                error = if (password.isNotBlank() && !passwordValidation.isValid) "Password doesn't meet requirements" else null
            )

            // Password Requirements
            if (showPasswordRequirements && password.isNotBlank()) {
                PasswordRequirementsUI(passwordValidation)
            }

            // Confirm Password Field
            TextField(
                value = confirmPassword,
                onValueChange = { confirmPassword = it },
                label = "Confirm Password",
                type = "password",
                placeholder = "Re-enter password",
                error = if (confirmPassword.isNotBlank() && password != confirmPassword) "Passwords don't match" else null
            )

            // Error Message
            error?.let { errorMessage ->
                P({
                    style {
                        color(Colors.Error)
                        fontSize(14.px)
                        marginBottom(16.px)
                        textAlign("center")
                    }
                }) {
                    Text(errorMessage)
                }
            }

            // Register Button
            Button(
                text = if (isLoading) "Creating Account..." else "Create Account",
                onClick = {
                    if (!isLoading && isFormValid) {
                        isLoading = true
                        error = null

                        scope.launch {
                            DependencyContainer.registerUseCase(
                                email = email,
                                password = password,
                                firstName = firstName,
                                lastName = lastName,
                                username = username.lowercase()
                            ).onSuccess {
                                isLoading = false
                                onRegisterSuccess()
                            }.onFailure { e ->
                                isLoading = false
                                error = e.message ?: "Registration failed"
                            }
                        }
                    }
                },
                enabled = !isLoading && isFormValid,
                fullWidth = true
            )

            // Login Link
            P({
                style {
                    textAlign("center")
                    marginTop(24.px)
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("Already have an account? ")
                A(
                    href = "#",
                    attrs = {
                        onClick { e ->
                            e.preventDefault()
                            onNavigateToLogin()
                        }
                        style {
                            color(Colors.Accent)
                            fontWeight(500)
                        }
                    }
                ) {
                    Text("Sign in")
                }
            }
        }
    }
}

/**
 * Displays password requirements with real-time validation feedback.
 */
@Composable
private fun PasswordRequirementsUI(validation: PasswordValidation) {
    Div({
        style {
            backgroundColor(Colors.SurfaceVariant)
            borderRadius(8.px)
            padding(12.px)
            marginBottom(16.px)
        }
    }) {
        P({
            style {
                fontSize(12.px)
                fontWeight(500)
                color(Colors.TextSecondary)
                marginBottom(8.px)
            }
        }) {
            Text("Password requirements:")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
            }
        }) {
            PasswordRequirementItem(
                label = "At least 8 characters",
                isMet = validation.hasMinLength
            )
            PasswordRequirementItem(
                label = "One uppercase letter (A-Z)",
                isMet = validation.hasUppercase
            )
            PasswordRequirementItem(
                label = "One lowercase letter (a-z)",
                isMet = validation.hasLowercase
            )
            PasswordRequirementItem(
                label = "One number (0-9)",
                isMet = validation.hasNumber
            )
            PasswordRequirementItem(
                label = "One special character (!@#$...)",
                isMet = validation.hasSpecialChar,
                isOptional = true
            )
        }
    }
}

@Composable
private fun PasswordRequirementItem(
    label: String,
    isMet: Boolean,
    isOptional: Boolean = false
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        // Status icon
        Span({
            style {
                fontSize(12.px)
                color(
                    when {
                        isMet -> Colors.Success
                        isOptional -> Colors.TextSecondary
                        else -> Colors.Error
                    }
                )
            }
        }) {
            Text(
                when {
                    isMet -> "\u2713" // Checkmark
                    isOptional -> "\u25CB" // Circle
                    else -> "\u2717" // X mark
                }
            )
        }

        // Label
        Span({
            style {
                fontSize(12.px)
                color(
                    when {
                        isMet -> Colors.Success
                        isOptional -> Colors.TextSecondary
                        else -> Colors.TextPrimary
                    }
                )
                if (isMet) {
                    property("text-decoration", "line-through")
                }
            }
        }) {
            Text(label)
            if (isOptional) {
                Text(" (optional)")
            }
        }
    }
}
